import {
  Box,
  Typography,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Button,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RouteList } from '../../routes/Constant';

import {
  type GridSortModel,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { AlertModal, CustomDataGrid } from 'components';
import { strings } from 'i18n/Strings';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  assignApprover,
  getAllCustomers,
  registerOnfidoVerification,
} from 'service/CustomerService';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { colorTheme } from 'theme';
import { checkSortingValues } from 'utils/GeneralUtil';
import { getAllSupportEmployeeList } from 'service/OperationalTeamService';
import { type SupportTeamEmployeeType } from 'types/services';
import { type RootState } from 'store';
import {
  setSnackBarFailed,
  setSnackBarInfo,
  setSnackBarSuccess,
} from 'store/snackBar';

interface ICustomerData {
  id: string;
  firstName: string;
  lastName: string;
  calling_code: string;
  phone: string;
  email: string;
  rows: number;
  count: number;
}
interface ICustomerResponse {
  basicInfo: {
    count: number;
    rows: ICustomerData[];
  };
}

type GridRowData = Record<string, unknown>;

const ReplacingPolicy = (): JSX.Element => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [customerData, setCustomerData] = useState<GridRowData[]>([]);
  const [openSortDropdown, setOpenSortDropDown] = useState(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  // const [sortBorderColor, setSortBorderColor] = useState('');
  const filterRef = useRef<HTMLElement>(null);
  const { empId } = useSelector((state: RootState) => state.onboarding);

  const sortRef = useRef<HTMLElement>(null);
  const pageInfo = useSelector(
    (state) => state?.pagination?.replacingPolicyRequestStatus,
  );
  const [supportEmployeeList, setSupportEmployeeList] = useState<
    SupportTeamEmployeeType[]
  >([]);
  const [searchValue, setSearchValue] = useState('');
  const [isOnfidoRegistrationOpen, setIsOnfidoRegistrationOpen] =
    useState(false);
  const [selectedOnfidoUserId, setSelectedOnfidoUserId] = useState<
    number | null
  >(null);
  const dispatch = useDispatch();
  const closeFilterDropdown = useCallback(
    (e: MouseEvent) => {
      if (
        filterRef.current &&
        openFilterDropdown &&
        !filterRef.current.contains(e.target as Node)
      ) {
        setOpenFilterDropdown(false);
      }
    },
    [openFilterDropdown],
  );

  const closeSortDropdown = useCallback(
    (e: MouseEvent) => {
      if (
        sortRef.current &&
        openSortDropdown &&
        !sortRef.current.contains(e.target as Node)
      ) {
        setOpenSortDropDown(false);
      }
    },
    [openSortDropdown],
  );

  useEffect(() => {
    document.addEventListener('mousedown', closeFilterDropdown);
  }, [closeFilterDropdown]);

  useEffect(() => {
    document.addEventListener('mousedown', closeSortDropdown);
  }, [closeSortDropdown]);

  const handleAssignUser = async (
    event: unknown,
    user: unknown,
  ): Promise<void> => {
    await assignApprover(Number(user.id), Number(event.target.value));
    void getCustomerData();
  };
  const getWatchlistHitLabel = (value: unknown): string => {
    if (value === null || typeof value === 'undefined') {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };
  const hasOnboardingChecklistId = (
    row: GridValidRowModel,
    targetId: number,
  ): boolean => {
    const checklistIds = row?.onboardingChecklistIds;
    if (!Array.isArray(checklistIds)) {
      return false;
    }
    return checklistIds.some((id) => Number(id) === targetId);
  };
  const handleOpenOnfidoRegistration = (userId: number): void => {
    setSelectedOnfidoUserId(userId);
    setIsOnfidoRegistrationOpen(true);
  };
  const handleOnfidoRegistrationConfirm = async (): Promise<void> => {
    if (selectedOnfidoUserId === null) {
      return;
    }
    try {
      await registerOnfidoVerification(selectedOnfidoUserId);
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.onfidoRegistrationSuccess,
        }),
      );
      void getCustomerData();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; msg?: string } };
      };
      const apiMessage = err.response?.data?.message ?? err.response?.data?.msg;
      dispatch(
        setSnackBarFailed({
          snackBarMessage:
            apiMessage ?? strings.displayText.onfidoRegistrationError,
        }),
      );
    } finally {
      setIsOnfidoRegistrationOpen(false);
      setSelectedOnfidoUserId(null);
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'id',
      width: 110,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.customersId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.id}
        </Typography>
      ),
    },
    {
      field: 'first_name',
      width: 180,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.name}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const hasOnboardingLogs =
          Array.isArray(row?.onboardingLogs) && row.onboardingLogs.length > 0;

        const message = row?.onboardingLogs
          .map(
            ({
              fieldName,
              action,
              oldValue,
              newValue,
            }: {
              fieldName: string;
              action: string;
              oldValue: string | number | boolean | null;
              newValue: string | number | boolean | null;
            }) =>
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              `${fieldName} ${action} from ${oldValue ?? ''} to ${
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                newValue ?? ''
              }`,
          )
          .join('\n');

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {row.first_name} {row.last_name}
            </Typography>
            {hasOnboardingLogs ? (
              <Tooltip title={message} placement="top">
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'assign',
      sortable: false,
      renderHeader: () => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          style={{ textAlign: 'center', marginLeft: 15 }}
        >
          Assign
        </Typography>
      ),
      width: 200,
      renderCell: ({ row }) => {
        const approverId = supportEmployeeList.find(
          (employee) => Number(employee.empId) === row.approverEmpId,
        );
        return (
          <FormControl fullWidth>
            <Select
              labelId="admin-detail-select-label"
              margin="normal"
              label="Assigned"
              value={approverId?.empId ?? ''}
              onChange={(event: unknown) => {
                void handleAssignUser(event, row);
              }}
            >
              {supportEmployeeList.map((element, i) => (
                <MenuItem key={i} value={element.empId}>
                  {element.firstName || ''} {element.lastName || ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },

    {
      field: 'fullPartialDeclaration',
      width: 240,
      align: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fullPartialDeclaration}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'center' }}
        >
          {row?.fullPartialDeclaration ? row?.fullPartialDeclaration : '-'}
        </Typography>
      ),
    },

    {
      field: 'approvalStatus',
      width: 270,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fullPartialDeclarationStatus}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const getStatus = (): string => {
          if (row.approvalStatus === 'Approved') {
            return strings.displayText.approved;
          }
          if (row.approvalStatus === 'Rejected') {
            return strings.displayText.rejected;
          }
          if (row.approvalStatus === 'In-progress') {
            return row.approverEmpId
              ? strings.displayText.inProgress
              : strings.displayText.new;
          }
          return strings.displayText.new;
        };

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {getStatus()}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: 'isAvcPensionUser',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {strings.displayText.avclabel}
          </Typography>
        </Box>
      ),
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row?.isAvcPensionUser ? 'Yes' : 'No'}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'avcApprovalStatus',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {strings.displayText.avcStatus}
          </Typography>
        </Box>
      ),
      renderCell: ({ row }) => {
        const getStatus = (): string => {
          if (row.avcApprovalStatus === 'UpdatedByAdmin') {
            return strings.displayText.new;
          }
          if (row.avcApprovalStatus === 'Approved') {
            return strings.displayText.approved;
          }
          if (row.avcApprovalStatus === 'Rejected') {
            return strings.displayText.rejected;
          }
          if (row.avcApproverEmpId) {
            return strings.displayText.inProgress;
          }
          return strings.displayText.new;
        };

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {getStatus()}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'watchlistPepHit',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.pepWatchlistHit}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'center' }}
        >
          {getWatchlistHitLabel(row?.watchlistPepHit)}
        </Typography>
      ),
    },
    {
      field: 'watchlistSanctionsHit',
      width: 210,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.sanctionsWatchlistHit}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'center' }}
        >
          {getWatchlistHitLabel(row?.watchlistSanctionsHit)}
        </Typography>
      ),
    },
    {
      field: 'legalWatchlistHit',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.legalWatchlistHit}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'center' }}
        >
          {getWatchlistHitLabel(row?.legalWatchlistHit)}
        </Typography>
      ),
    },
    {
      field: 'identityVerified',
      width: 170,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.identityVerified}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'center' }}
        >
          {hasOnboardingChecklistId(row, 1) ? 'Yes' : 'No'}
        </Typography>
      ),
    },
    {
      field: 'addressVerified',
      width: 170,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.addressVerified}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'center' }}
        >
          {hasOnboardingChecklistId(row, 2) ? 'Yes' : 'No'}
        </Typography>
      ),
    },
    {
      field: 'onfidoRegistration',
      width: 190,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.onfidoRegistration}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            sx={{
              height: 32,
              minWidth: 96,
              bgcolor: colors.greenAccent[500],
              borderRadius: 2,
              textTransform: 'none',
            }}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              handleOpenOnfidoRegistration(Number(row.id));
            }}
          >
            {strings.displayText.registerLabel}
          </Button>
        </Box>
      ),
    },
  ];

  const getCustomerData = async (): Promise<void> => {
    try {
      const res = await getAllCustomers({
        page: Number(pageInfo?.page) + 1,
        size: pageInfo?.pageSize,
        sortBy: pageInfo?.sortBy,
        sortOrder: pageInfo?.sortOrder,
        searchValue,
      });
      const customerInfo = res as unknown as ICustomerResponse;
      setCustomerData(customerInfo?.basicInfo?.rows);
      setPageCount(customerInfo?.basicInfo?.count);
    } catch (error) {}
  };
  useEffect(() => {
    void getSupportEmployeeList();
  }, []);

  const getSupportEmployeeList = async (): Promise<void> => {
    getAllSupportEmployeeList()
      .then((res) => {
        if (typeof res !== 'undefined' && 'data' in res) {
          const { data } = res as {
            data: SupportTeamEmployeeType[];
          };
          if (data) {
            setSupportEmployeeList(data);
          }
        }
      })
      .catch((err) => err);
  };

  useEffect(() => {
    void getCustomerData();
  }, [
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    searchValue,
  ]);

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'replacingPolicyRequestStatus', value: 0 }));
  };

  const handleSortChange = (model: GridSortModel): void => {
    if (model.length === 0 || !model[0].sort) {
      dispatch(
        setSortBy({
          key: 'replacingPolicyRequestStatus',
          value: '',
        }),
      );
      dispatch(
        setSortOrder({
          key: 'replacingPolicyRequestStatus',
          value: '',
        }),
      );
      dispatch(
        setCurrentPage({ key: 'replacingPolicyRequestStatus', value: 0 }),
      );
      return;
    }

    const sortFieldMap: Record<string, string> = {
      id: 'id',
      firstName: 'first_name',
      assign: 'approverEmpId',
      fullPartialDeclaration: 'fullPartialDeclaration',
      declarationStatus: 'approvalStatus',
      avc: 'isAvcPensionUser',
      avcStatus: 'avcApprovalStatus',
      watchlistPepHit: 'watchlistPepHit',
      watchlistSanctionsHit: 'watchlistSanctionsHit',
      legalWatchlistHit: 'legalWatchlistHit',
      identityVerified: 'identityVerified',
      addressVerified: 'addressVerified',
    };

    dispatch(
      setSortBy({
        key: 'replacingPolicyRequestStatus',
        value: sortFieldMap[model[0].field] ?? model[0].field,
      }),
    );
    dispatch(
      setSortOrder({
        key: 'replacingPolicyRequestStatus',
        value: model[0].sort,
      }),
    );
    dispatch(setCurrentPage({ key: 'replacingPolicyRequestStatus', value: 0 }));
  };
  return (
    <Fragment>
      <Box sx={{ cursor: 'pointer' }}>
        <CustomDataGrid
          title="Onboarding Status"
          columns={columns}
          rows={customerData}
          pageCount={pageCount}
          searchEnabled={true}
          handleSearch={handleSearch}
          getRowId={(row: GridRowData) => `${String(row.id)}`}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          onRowClick={(row) => {
            if (empId === row?.row?.approverEmpId) {
              navigate(RouteList.OnboardingByUser.replace(':userId', row.id));
            } else {
              dispatch(
                setSnackBarInfo({
                  snackBarMessage:
                    strings.displayText.noPermissionToSeeOnBoradRequet,
                }),
              );
            }
          }}
          currentPage="replacingPolicyRequestStatus"
          sortModel={checkSortingValues(pageInfo)}
          sortingMode={strings.displayText.sortingServer}
          onSortModelChange={(mode) => {
            handleSortChange(mode);
          }}
        />
      </Box>
      <AlertModal
        open={isOnfidoRegistrationOpen}
        onClose={() => {
          setIsOnfidoRegistrationOpen(false);
          setSelectedOnfidoUserId(null);
        }}
        title={`${strings.displayText.onfidoRegistrationConfirmation}`}
        isButtonForConfirmation={true}
        handleConfirm={handleOnfidoRegistrationConfirm}
      />
    </Fragment>
  );
};

export default ReplacingPolicy;
